import { FormProvider, useForm } from 'react-hook-form';

import { useNavigate, useParams } from 'react-router';

import { styled } from '@mui/material';

import { useEffect, useState } from 'react';

import { useI18n } from '@app-velocity/i18n';

import { Box, Form, Button } from '@app-velocity/ui';

import { ClassValidatorResolver } from '@app-velocity/validators';

import {
  ScenarioRoutePaths,
  useUserSimulationControl,
} from '@app-velocity/modules/scenarios';

import { TPrototypeDto } from '@app-velocity/av-api-v2';

import { AuthRoutesPaths, useCurrentUser } from '@app-velocity/libs/auth';

import { useCurrentTeamPermissions } from '@app-velocity/libs/team-permissions';

import { useFlows } from '../../../../../hooks';

import { PrimaryPage } from '../../../../../layouts';

import { CreateScenarioDto } from '../../../../../dto/CreateScenarioDto';

import { ConfigureScenarioForm } from './components/ConfigureScenarioForm';
import { useConfigureScenario } from './hooks/useConfigureScenario';
import { LoadingScreen } from './components/LoadingScreen';
import { useConfigureScenarioContext } from './context/useConfigureScenarioContext';
import { ConfigureScenarioPageSkeleton } from './components/ConfigureScenarioPageSkeleton';
import { ConfigureScenarioError } from './components/ConfigureScenarioError';
import { getPageAndFileData } from '../../../../../../core';

const ConfigureScenarioActions = styled(Box)({
  paddingTop: '12px',
  display: 'flex',
  flexWrap: 'nowrap',
  gap: '12px',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  flex: 1,
});

export const ConfigureScenario = () => {
  const { t } = useI18n();
  const { workspaceId, teamId, mode, challengeAFlow, challengeSimulation } =
    useConfigureScenarioContext();
  const currentUser = useCurrentUser();
  const currentTeam = useCurrentTeamPermissions();
  const { nodeId } = useParams<{
    nodeId: string;
  }>();
  const { flows, loading } = useFlows();
  const {
    saveScenarioWithPages,
    abortSaving,
    saveError,
    isBaseScenarioPending,
    isScenarioContentCreating,
    pagesCount,
    savedPagesCount,
    partialsCount,
    savedPartialsCount,
    finishedCount,
    totalToFinish,
  } = useConfigureScenario();
  const { start } = useUserSimulationControl(mode);
  const [challengeLoading, setChallengeLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const methods = useForm<CreateScenarioDto>({
    mode: 'onSubmit',
    resolver: ClassValidatorResolver(CreateScenarioDto),
    defaultValues: {
      amount: '1',
    },
  });

  const { handleSubmit } = methods;

  useEffect(() => {
    const currentFlow = flows?.some((flow) => flow.nodeId === nodeId);

    if (flows && !currentFlow) {
      navigate(challengeAFlow(teamId, workspaceId!));
    }
  }, [flows]);

  const getChallengeTitle = () => {
    if (!flows) return null;

    const currentFlow = flows.find((flow) => flow.nodeId === nodeId);

    if (!currentFlow) return null;

    return currentFlow.name;
  };

  const startSimulation = async (scenario: TPrototypeDto, amount: number) => {
    if (!scenario || !scenario.id || !workspaceId || !teamId) return;

    if (!currentUser || !currentUser.authorized) {
      navigate(AuthRoutesPaths.Login);

      return;
    }

    if (!currentTeam || !currentTeam.canRunSimulations(amount)) {
      return;
    }

    // reviewId for single, null for extended
    const reviewIdOrNull = await start(
      teamId,
      workspaceId,
      scenario.id!,
      'default',
      amount
    );

    if (amount === 1) {
      if (!reviewIdOrNull) return;
      navigate(
        challengeSimulation(teamId, workspaceId, scenario.id, reviewIdOrNull)
      );
    } else {
      navigate(
        ScenarioRoutePaths.challengeDetails(teamId, workspaceId, scenario.id)
      );
    }
  };

  const abort = () => {
    abortSaving();
    methods.reset();
    setChallengeLoading(false);

    // navigate(challengeAFlow(workspaceId));
  };

  const onSubmit = async (data: CreateScenarioDto) => {
    if (!currentUser || !currentUser.authorized) {
      navigate(AuthRoutesPaths.Login);

      return;
    }

    if (!currentTeam || !currentTeam.canRunSimulations(parseInt(data.amount)))
      return;

    const title = getChallengeTitle();

    if (!title) return;

    setChallengeLoading(true);
    const { pageName, fileName, fileId } = await getPageAndFileData();
    const publishedScenario = await saveScenarioWithPages(
      {
        introMessage: data.introMessage,
        pageName,
        fileName,
        sourceId: fileId,
        name: title,
      },
      data.dontSkipVariants
    );

    if (publishedScenario) {
      startSimulation(publishedScenario, parseInt(data.amount));
    } else {
      methods.reset();
    }
  };

  const goBack = () => {
    if (!workspaceId || !teamId) return;
    navigate(challengeAFlow(teamId, workspaceId));
  };

  useEffect(() => {
    if (!saveError) return;

    setChallengeLoading(false);
  }, [saveError]);

  if (challengeLoading)
    return (
      <LoadingScreen
        savedPagesCount={savedPagesCount}
        pagesCount={pagesCount}
        partialsCount={partialsCount}
        savedPartialsCount={savedPartialsCount}
        finishedCount={finishedCount}
        totalToFinish={totalToFinish}
        abort={abort}
      />
    );

  if (loading) return <ConfigureScenarioPageSkeleton />;

  return (
    <PrimaryPage
      back={challengeAFlow(teamId!, workspaceId!)}
      backLabel={t('prototypes:select_flow')}
      title={t('prototypes:configure_scenario')}
    >
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ConfigureScenarioForm />
          <ConfigureScenarioError error={saveError} />
          <ConfigureScenarioActions>
            <Button variant="contained" color="secondary" onClick={goBack}>
              {t('shared:cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={isScenarioContentCreating || isBaseScenarioPending}
              type="submit"
            >
              {t('shared:create')}
            </Button>
          </ConfigureScenarioActions>
        </Form>
      </FormProvider>
    </PrimaryPage>
  );
};
